import React, { useEffect, useMemo, useState } from 'react';
import './ScormAssign.css';
import { showToast } from './Toast';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { sortScorms, scormOptionLabel } from '../utils/scormHelpers';

const ScormPreview = ({ onOpenModal }) => {
  const [scorms, setScorms] = useState([]);
  const [scormSort, setScormSort] = useState('alpha');
  const [previewScorm, setPreviewScorm] = useState('');
  const [message, setMessage] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [rollbackNote, setRollbackNote] = useState('');

  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_BASE || '';
    const load = async () => {
      try {
        const token = sessionStorage.getItem('accessToken');
        const res = await fetch(`${API_BASE}/admin/scorm/list`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (!res.ok) throw new Error('No autorizado');
        const data = await res.json();
        setScorms(data.scorms || []);
      } catch (err) {
        setMessage('No se pudieron cargar cursos');
      }
    };
    load();
    const onUploaded = () => load();
    window.addEventListener('scorm:uploaded', onUploaded);
    return () => window.removeEventListener('scorm:uploaded', onUploaded);
  }, []);

  const selectedScorm = useMemo(() => {
    return (scorms || []).find(s => String(s.id) === String(previewScorm)) || null;
  }, [scorms, previewScorm]);

  const lastChangeText = useMemo(() => {
    if (!selectedScorm || !Array.isArray(selectedScorm.versions) || selectedScorm.versions.length === 0) return '';
    const latest = selectedScorm.versions
      .slice()
      .sort((a, b) => Number((b && b.version) || 0) - Number((a && a.version) || 0))[0];
    if (!latest) return '';
    const note = latest.note ? String(latest.note).trim() : '';
    if (latest.rolledBackFrom != null) {
      return `Rollback desde v${latest.rolledBackFrom}${note ? ` — ${note}` : ''}`;
    }
    const isFirstVersion = Number(latest.version) === 1;
    if (isFirstVersion) {
      return note ? `Primera versión — ${note}` : 'Primera versión';
    }
    return note ? `Actualización — ${note}` : 'Actualización';
  }, [selectedScorm]);

  const rollbackToVersion = async (targetVersion) => {
    if (!previewScorm) return;
    const v = Number(targetVersion);
    if (!Number.isFinite(v) || v <= 0) return;

    const nextV = Number((selectedScorm && selectedScorm.version) || 1) + 1;
    const r = await Swal.fire({
      title: 'Restaurar versión',
      text: `¿Restaurar el curso a la versión v${v}? Esto creará una nueva versión (v${nextV}) basada en esa versión anterior.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, restaurar',
      cancelButtonText: 'Cancelar'
    });
    if (!r.isConfirmed) return;

    const toastId = showToast.loading(`Aplicando rollback a v${v}...`);
    try {
      const token = sessionStorage.getItem('accessToken');
      const res = await fetch(`${process.env.REACT_APP_API_BASE || ''}/admin/scorm/rollback`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ scormId: previewScorm, targetVersion: v, note: rollbackNote })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || 'Error aplicando rollback');

      showToast.success(data.message || `Rollback aplicado desde v${v}`);
      setRollbackNote('');
      setShowHistoryModal(false);

      // recargar lista
      try { window.dispatchEvent(new CustomEvent('scorm:uploaded', { detail: data.scorm })); } catch(e) {}
    } catch (e) {
      showToast.error(e.message || 'Error');
    } finally {
      toast.dismiss(toastId);
    }
  };

  const openPreview = async () => {
    if (!previewScorm) return setMessage('Seleccione SCORM para previsualizar');
    const token = sessionStorage.getItem('accessToken');
    if (!token) return setMessage('No hay token de sesión');
    const s = scorms.find(x => x.id === previewScorm);
    const id = s ? s.id : previewScorm;
    // Usar URL directa de EB (más confiable que el proxy de Apache)
    const ebBase = process.env.REACT_APP_EB_BASE || '';

    // El token de sesión no puede viajar en la URL (CWE-598), así que primero
    // se canjea por un ticket de un solo propósito que vive 60 segundos.
    try {
      const res = await fetch(`${ebBase}/admin/scorm/preview-ticket/${id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return setMessage('No se pudo abrir la previsualización');
      const { ticket } = await res.json();
      if (!ticket) return setMessage('No se pudo abrir la previsualización');

      const cacheBuster = Date.now();
      const url = `${ebBase}/admin/scorm/preview/${id}?t=${encodeURIComponent(ticket)}&_=${cacheBuster}`;
      window.open(url, '_blank', 'width=1400,height=900');
    } catch (e) {
      setMessage('No se pudo abrir la previsualización');
    }
  };

  const openExternal = () => {
    if (!previewScorm) return setMessage('Seleccione SCORM para previsualizar');
    const s = scorms.find(x => x.id === previewScorm);
    const id = s ? s.id : previewScorm;
    const apiBase = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');
    const url = `${apiBase}/scorm/launch/${id}`;
    window.open(url, '_blank');
  };

  const getLaunchUrl = () => {
    if (!previewScorm) return '';
    const s = scorms.find(x => x.id === previewScorm);
    const apiBase = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');
    const id = s ? s.id : previewScorm;
    return `${apiBase}/scorm/launch/${id}`;
  };

  const copyLaunchUrl = async () => {
    const url = getLaunchUrl();
    if (!url) return setMessage('Seleccione SCORM para copiar link');
    try {
      await navigator.clipboard.writeText(url);
      showToast.success('Link copiado');
    } catch (e) {
      setMessage('No se pudo copiar el link');
    }
  };

  const openInModal = () => {
    if (!previewScorm) return setMessage('Seleccione SCORM para previsualizar');
    if (typeof onOpenModal === 'function') {
      onOpenModal(previewScorm);
    } else {
      openExternal();
    }
  };

  const [isDeleting, setIsDeleting] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  const startRename = () => {
    if (!previewScorm || !selectedScorm) return setMessage('Seleccione SCORM para renombrar');
    setRenameValue(selectedScorm.title || '');
    setIsRenaming(true);
  };

  const cancelRename = () => {
    setIsRenaming(false);
    setRenameValue('');
  };

  const confirmRename = async () => {
    const newTitle = (renameValue || '').trim();
    if (!newTitle) return showToast.error('El título no puede estar vacío');
    if (!previewScorm) return;

    const toastId = showToast.loading('Renombrando curso...');
    try {
      const token = sessionStorage.getItem('accessToken');
      const res = await fetch(`${process.env.REACT_APP_API_BASE || ''}/admin/scorm/rename`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ scormId: previewScorm, title: newTitle })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Error renombrando curso');

      showToast.success('Curso renombrado');
      setIsRenaming(false);
      setRenameValue('');
      // Recargar lista
      try { window.dispatchEvent(new CustomEvent('scorm:uploaded')); } catch (e) {}
    } catch (e) {
      showToast.error(e.message || 'Error');
    } finally {
      toast.dismiss(toastId);
    }
  };

  const deleteCourse = async () => {
    if (!previewScorm) return setMessage('Seleccione SCORM para eliminar');
    const r = await Swal.fire({ title: 'Eliminar curso', text: '¿Eliminar curso SCORM y todas sus asignaciones? Esta acción no se puede deshacer.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Sí, eliminar', cancelButtonText: 'Cancelar', confirmButtonColor: '#dc2626' });
    if (!r.isConfirmed) return;
    setIsDeleting(true);
    const toastId = showToast.loading('Eliminando SCORM...');
    try {
      const token = sessionStorage.getItem('accessToken');
      const res = await fetch(`${process.env.REACT_APP_API_BASE || ''}/admin/scorm/${previewScorm}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const errBody = await res.json().catch(()=>({}));
        throw new Error(errBody.error || 'Error eliminando SCORM');
      }
      showToast.success('SCORM eliminado');
      // notificar a otros componentes
      try { window.dispatchEvent(new CustomEvent('scorm:uploaded')); } catch(e){}
      try { window.dispatchEvent(new CustomEvent('scorm:changed', { detail: { id: previewScorm } })); } catch(e){}
      // cerrar modal si es abierto
      if (typeof onOpenModal === 'function') onOpenModal(null);
    } catch (err) {
      showToast.error(err.message || 'Error');
      setMessage(err.message || 'Error');
    } finally {
      setIsDeleting(false);
      toast.dismiss(toastId);
    }
  };

  return (
  <div className="scorm-admin-card col-12 text-left">
    <div className="scorm-card">
      <div className="scorm-card__header">
        <div className="scorm-card__title">Previsualizar</div>
        <div className="scorm-card__subtitle">Abrir en modal o en nueva pestaña</div>
      </div>
      <div className="scorm-card__body">
        <div className="scorm-row">
          <div className="scorm-field">
            <label>Curso SCORM</label>
            <div className="scorm-sort-toggle">
              <button type="button" className={`scorm-sort-btn ${scormSort === 'alpha' ? 'scorm-sort-btn--active' : ''}`} onClick={() => setScormSort('alpha')}>A-Z</button>
              <button type="button" className={`scorm-sort-btn ${scormSort === 'recent' ? 'scorm-sort-btn--active' : ''}`} onClick={() => setScormSort('recent')}>Recientes</button>
            </div>
            <select className="scorm-select" value={previewScorm} onChange={e => { setPreviewScorm(e.target.value); setMessage(''); }}>
              <option value="">-- Seleccionar --</option>
              {sortScorms(scorms, scormSort).map(s => (
                <option key={`pv-${s.id}`} value={s.id}>
                  {scormOptionLabel(s)}{s && s.version ? ` (v${s.version})` : ''}
                </option>
              ))}
            </select>
            {previewScorm && (
              <div className="scorm-meta" style={{ wordBreak: 'break-all' }}>
                <strong>Link:</strong> {getLaunchUrl()}
                {selectedScorm && (
                  <div style={{ marginTop: 6, opacity: 0.9 }}>
                    <strong>Versión:</strong> {selectedScorm.version ? `v${selectedScorm.version}` : 'v1'}
                    {selectedScorm.updatedAt ? ` • ${String(selectedScorm.updatedAt)}` : ''}
                    {lastChangeText ? (
                      <div style={{ marginTop: 4 }}><strong>Último cambio:</strong> {lastChangeText}</div>
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="scorm-actions">
          <button type="button" className="scorm-btn scorm-btn--primary" onClick={openPreview} disabled={isDeleting || !previewScorm}>
            Vista Previa (Admin)
          </button>
          <button type="button" className="scorm-btn scorm-btn--secondary" onClick={openInModal} disabled={isDeleting || !previewScorm}>
            Abrir en modal
          </button>
          <button type="button" className="scorm-btn scorm-btn--secondary" onClick={openExternal} disabled={isDeleting || !previewScorm}>
            Abrir externo
          </button>
          {/* <button type="button" className="scorm-btn scorm-btn--secondary" onClick={copyLaunchUrl} disabled={isDeleting || !previewScorm}>
            Copiar link
          </button> */}
          <button type="button" className="scorm-btn scorm-btn--secondary" onClick={startRename} disabled={isDeleting || !previewScorm}>
            Renombrar
          </button>
          <button
            type="button"
            className="scorm-btn scorm-btn--secondary"
            onClick={() => setShowHistoryModal(true)}
            disabled={isDeleting || !previewScorm}
          >
            Historial
          </button>
          <button type="button" className="scorm-btn scorm-btn--danger" onClick={deleteCourse} disabled={isDeleting || !previewScorm}>
            {isDeleting ? 'Eliminando...' : 'Eliminar'}
          </button>
        </div>

        {isRenaming && (
          <div style={{ marginTop: 12, padding: '12px 16px', background: '#f8f9fa', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>Nuevo nombre del curso</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="text"
                className="scorm-select"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') confirmRename(); if (e.key === 'Escape') cancelRename(); }}
                autoFocus
                style={{ flex: 1 }}
              />
              <button type="button" className="scorm-btn scorm-btn--primary" onClick={confirmRename} disabled={!renameValue.trim()}>Guardar</button>
              <button type="button" className="scorm-btn scorm-btn--secondary" onClick={cancelRename}>Cancelar</button>
            </div>
          </div>
        )}

        {showHistoryModal && selectedScorm && (
          <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', overflowY: 'auto' }}>
            <div className="modal-dialog modal-lg" style={{ marginTop: '8vh' }}>
              <div className="modal-content" style={{ borderRadius: '12px' }}>
                <div className="modal-header" style={{
                  background: 'linear-gradient(135deg, #1a1a1a, #2d2d2d)',
                  color: 'white',
                  borderRadius: '12px 12px 0 0'
                }}>
                  <h5 className="modal-title" style={{ margin: 0 }}>Historial de versiones</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowHistoryModal(false)}></button>
                </div>

                <div className="modal-body" style={{ background: '#fff' }}>
                  <div className="mb-2" style={{ opacity: 0.9 }}>
                    <div><strong>Curso:</strong> {selectedScorm.title || selectedScorm.id}</div>
                    <div><strong>Versión activa:</strong> {selectedScorm.version ? `v${selectedScorm.version}` : 'v1'}</div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label"><strong>Motivo (opcional)</strong></label>
                    <input
                      className="form-control"
                      type="text"
                      value={rollbackNote}
                      onChange={(e) => setRollbackNote(e.target.value)}
                      placeholder="Ej: Se detectó error en contenido / volver a versión estable"
                    />
                    <small className="text-muted">Se guarda en el historial del rollback.</small>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-sm table-striped">
                      <thead>
                        <tr>
                          <th style={{ width: 90 }}>Versión</th>
                          <th>Creada</th>
                          <th>Último cambio</th>
                          <th>Detalle</th>
                          <th style={{ width: 140 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(Array.isArray(selectedScorm.versions) ? selectedScorm.versions : [])
                          .slice()
                          .sort((a, b) => Number((b && b.version) || 0) - Number((a && a.version) || 0))
                          .map((ver) => {
                            const verNum = Number(ver && ver.version);
                            const isActive = Number(selectedScorm.version || 1) === verNum;
                            const isRollback = ver && ver.rolledBackFrom != null;
                            const note = ver && ver.note ? String(ver.note) : '';
                            const changeText = isRollback ? `Rollback desde v${ver.rolledBackFrom}` : (verNum === 1 ? 'Primera versión' : 'Actualización');
                            return (
                              <tr key={String(verNum)}>
                                <td><strong>{Number.isFinite(verNum) ? `v${verNum}` : '-'}</strong>{isActive ? ' (activa)' : ''}</td>
                                <td>{ver && ver.createdAt ? String(ver.createdAt) : '-'}</td>
                                <td>{changeText}</td>
                                <td>
                                  {note ? <div style={{ opacity: 0.85 }}>{note}</div> : '-'}
                                </td>
                                <td className="text-end">
                                  <button
                                    type="button"
                                    className="scorm-btn scorm-btn--primary"
                                    onClick={() => rollbackToVersion(verNum)}
                                    disabled={isDeleting || isActive || !Number.isFinite(verNum)}
                                    title={isActive ? 'Ya es la versión activa' : 'Restaurar a esta versión (crea una nueva versión)'}
                                  >
                                    Restaurar
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>

                  <small className="text-muted">
                    “Restaurar” crea una nueva versión con el contenido restaurado.
                  </small>
                </div>

                <div className="modal-footer" style={{
                  padding: '15px 20px',
                  borderTop: '1px solid #eee',
                  borderRadius: '0 0 12px 12px',
                  background: '#f8f9fa'
                }}>
                  <button
                    type="button"
                    className="scorm-btn scorm-btn--secondary"
                    onClick={() => setShowHistoryModal(false)}
                    disabled={isDeleting}
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {message && (
          <div className={`scorm-message ${String(message).toLowerCase().includes('no se pudo') || String(message).toLowerCase().includes('error') ? 'scorm-message--error' : ''}`}>{message}</div>
        )}
      </div>
    </div>
  </div>
  );
};

export default ScormPreview;
